import React, { useReducer, useEffect } from 'react'
import { RouterProvider } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary'
import ParticipantAuthenticationRouter from './routes'
import { TranslateContext } from "../../../widgets/MyDataHelps/TranslateContext";
import { AuthenticationContext, AuthenticationDispatchContext } from './AuthenticationContext';
import {
  AuthenticationConfigurationContext,
  defaultParticipantLoginConfiguration,
  reducer as configurationReducer,
} from './Configuration';
import { defaultState, reducer, AuthenticationViews } from './Store';
import {
  LoginLink,
  Locale,
} from '../types';
import { getProjectCode, getQueryParams, } from '../utils';

function fallbackRender(props: any) {
  // Call resetErrorBoundary() to reset the error boundary and retry the render.

  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre style={{ color: "red" }}>{props.error.message}</pre>
    </div>
  );
}

export interface AuthenticationProps {
  availableLocales: Locale[];
  cookies: any;
  currentLanguage: string;
  externalLoginLinks: LoginLink[];
  hasIdentityServerLogin: boolean;
  lastIdp: string;
  lastLoginLink: LoginLink;
  lastSessionCookie: string;
  nativeAppRedirectUrl: string;
  productName: string;
  redirectUrl: string;
  reloginState: string;
  sessionCookieName: string;
  translate: any;
}

const App: React.FC<AuthenticationProps> = (props) => {
  // Direct to a project splash page if a project code is present
  const project = getProjectCode()
  if (project && !window.location.hash) {
    window.location.hash = AuthenticationViews.splash;
  }

  const [state, dispatch] = useReducer(reducer, {
    ...defaultState,
    activeView: project ? AuthenticationViews.splash : defaultState.activeView,
    currentLanguage: props.currentLanguage,
    lastSessionCookie: props.lastSessionCookie,
    redirectUrl: props.redirectUrl,
  });
  const [configurationState] = useReducer(configurationReducer, {
    ...defaultParticipantLoginConfiguration,
    availableLocales: props.availableLocales,
    cookies: props.cookies,
    externalLoginLinks: props.externalLoginLinks,
    hasIdentityServerLogin: props.hasIdentityServerLogin,
    lastIdp: props.lastIdp,
    lastLoginLink: props.lastLoginLink,
    nativeAppRedirectUrl: props.nativeAppRedirectUrl,
    productName: props.productName,
    reloginState: props.reloginState,
    sessionCookieName: props.sessionCookieName,
    translate: props.translate
  });

  // Translation Related Effect
  useEffect(() => {
    async function translateChangeEnd() {
      const lang = await props.translate.use();
      dispatch({ type: 'SET_CURRENT_LANGUAGE', payload: lang })
    }

    async function changeLanguage(lang: string) {
      try {
        await props.translate.use(lang)
        // Remove the language parameter from the query string since we store that in a cookie
        var currentUri = new URL(window.location.href);
        if (currentUri.searchParams.has("lang")) {
          currentUri.searchParams.delete("lang");
          window.history.replaceState({}, document.title, currentUri.toString());
        }
      } catch (error) {
        console.error("Error changing language", error);
      }
    }

    const language = getQueryParams().get("lang")
    if (language) {
      changeLanguage(language)
        .then(() => {
          dispatch({ type: 'SET_CURRENT_LANGUAGE', payload: language })
        })
        .catch((error) => {
          console.error("Error changing language", error);
        })
    } else {
      window.addEventListener("$translateChangeEnd", translateChangeEnd);
    }
    return () => {
      window.removeEventListener("$translateChangeEnd", translateChangeEnd);
    }
  }, [])

  return (
    <ErrorBoundary fallbackRender={fallbackRender} >
      <TranslateContext.Provider value={props.translate}>
        <AuthenticationConfigurationContext.Provider value={configurationState}>
          <AuthenticationContext.Provider value={state}>
            <AuthenticationDispatchContext.Provider value={dispatch}>
              <RouterProvider router={ParticipantAuthenticationRouter} />
            </AuthenticationDispatchContext.Provider>
          </AuthenticationContext.Provider>
        </AuthenticationConfigurationContext.Provider>
      </TranslateContext.Provider >
    </ErrorBoundary >
  )
}

export default App
